import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { api } from "../../lib/api";
import { useSite } from "../../lib/site";
import { Logo } from "../Logo";
import { LockKeyhole, Menu, X, Phone, Mail, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";

export function useAgency() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/public/agency").then((r) => setA(r.data)).catch(() => setA({})); }, []);
  return a;
}

function Nav() {
  const { t, lang, setLang } = useSite();
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const on = () => setSolid(window.scrollY > 40);
    on(); window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  const links = [
    { to: "/propriedades?purpose=venda", label: t("nav_buy"), id: "buy" },
    { to: "/propriedades?purpose=arrendamento", label: t("nav_rent"), id: "rent" },
    { to: "/#avaliacao", label: t("nav_sell"), id: "sell" },
    { to: "/#contactos", label: t("nav_contact"), id: "contact" },
  ];
  return (
    <header className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${solid || open ? "bg-[#0D1B2A]/95 backdrop-blur-md border-b border-[#1E3A56]" : "bg-gradient-to-b from-black/50 to-transparent"}`} data-testid="site-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="site-logo"><span className="hidden sm:block"><Logo size="sm" /></span><span className="sm:hidden"><Logo size="sm" showText={false} /></span></Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => <Link key={l.id} to={l.to} data-testid={`site-nav-${l.id}`} className="text-sm text-slate-200 hover:text-[#E0C574] transition-colors">{l.label}</Link>)}
        </nav>
        <div className="flex items-center gap-3">
          <button onClick={() => setLang(lang === "pt" ? "en" : "pt")} data-testid="site-lang-toggle" className="text-xs font-semibold tracking-widest text-slate-300 hover:text-[#E0C574] border border-white/20 rounded-full px-3 py-1.5">
            {lang === "pt" ? "EN" : "PT"}
          </button>
          <NavLink to="/login" data-testid="site-portal-btn" className="inline-flex items-center gap-2 bg-[#C9A84C] hover:bg-[#E0C574] text-[#0D1B2A] text-sm font-semibold rounded-full px-5 py-2 transition-colors">
            <LockKeyhole size={14} /> {t("portal")}
          </NavLink>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="site-mobile-menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </div>
      {open && (
        <div className="md:hidden px-5 pb-5 flex flex-col gap-3">
          {links.map((l) => <Link key={l.id} to={l.to} onClick={() => setOpen(false)} className="text-slate-200 py-1">{l.label}</Link>)}
        </div>
      )}
    </header>
  );
}

function Footer({ agency }) {
  const { t } = useSite();
  const a = agency || {};
  const socials = [["instagram", Instagram], ["facebook", Facebook], ["linkedin", Linkedin]].filter(([k]) => a[k]);
  return (
    <footer id="contactos" className="bg-[#08121D] border-t border-[#1E3A56] text-slate-400" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <Logo size="md" />
          <p className="text-sm mt-4 max-w-xs">{t("contact_sub")}</p>
        </div>
        <div className="space-y-3 text-sm">
          <p className="text-[#C9A84C] uppercase tracking-[0.25em] text-xs">{t("contact_title")}</p>
          <p className="flex items-center gap-2" data-testid="agency-phone"><Phone size={14} /> {a.phone}</p>
          <p className="flex items-center gap-2" data-testid="agency-email"><Mail size={14} /> {a.email}</p>
          <p className="flex items-center gap-2" data-testid="agency-address"><MapPin size={14} /> {a.address}</p>
          {socials.length > 0 && <div className="flex gap-3 pt-2">{socials.map(([k, I]) => <a key={k} href={a[k]} target="_blank" rel="noreferrer" className="hover:text-[#E0C574]"><I size={18} /></a>)}</div>}
        </div>
        <div className="text-xs space-y-2 md:text-right">
          <p data-testid="agency-ami">{t("ami")}: {a.ami}</p>
          <p>{t("privacy")}</p>
          <p>© {new Date().getFullYear()} {a.name}. {t("rights")}</p>
        </div>
      </div>
    </footer>
  );
}

export function SiteLayout({ children }) {
  const agency = useAgency();
  return (
    <div className="min-h-screen bg-[#0D1B2A] text-slate-100" data-testid="public-site">
      <Nav />
      <main>{children}</main>
      <Footer agency={agency} />
    </div>
  );
}
